import clsx from 'clsx';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import useConfirm from '@/src/components/common/Modal/ConfirmModal/useConfirm';
import { showToast } from '@/src/globalFunction';
import { RootState } from '@/src/reducers/types';

import HeaderAction from './HeaderAction/HeaderAction';
import AutomationSettings from './InboxItemSettings/AutomationSettings/AutomationSettings';
import InboxItemSettings from './InboxItemSettings/InboxItemSettings';
import PrefillSettings from './InboxItemSettings/PrefillSettings/PrefillSettings';

import SettingsCard from '../../../Elements/SettingsCard/SettingsCard';
import { useSaveInboxSetting } from '../api/inboxSetting';
import {
  SettingCategoryProp,
  inboxLayout,
  inboxRequiredAction,
  inboxIsAutomationEnabled,
} from '../types';

const Inbox = ({
  onCancel,
  triggerEdit,
  isOpen,
  isEdit,
  hasChanges,
  editChange,
}: SettingCategoryProp) => {
  const project = useSelector((store: RootState) => store.project);
  const loggedUser = useSelector((store: RootState) => store.loggedUser);

  const [layout, setLayout] = useState<inboxLayout>('view');
  const [requiredAction, setRequiredAction] = useState<inboxRequiredAction>(0);
  const [isAutomationEnabled, setIsAutomationEnabled] = useState<inboxIsAutomationEnabled>(0);
  const [isLineSuggestionsEnabled, setIsLineSuggestionsEnabled] = useState<inboxRequiredAction>(0);

  const dispatch = useDispatch();

  const saveInboxSettingMutation = useSaveInboxSetting({
    selectedId: project.Selected.id,
    params: { requiredAction: 0, isAutomationEnabled: 0, isLineSuggestionsEnabled: 0 },
  });

  const [DiscardChangesModal, confirmDiscardChanges] = useConfirm({
    classes: '',
    modalWidth: '600px',
    title: 'Discard Changes?',
    message: `Do you wish to proceed?`,
    confirmBtn: {
      label: 'Discard',
    },
  });

  const onDiscardChanges = async () => {
    const selected = project.Selected;

    const hasRecentChanges =
      selected.requiredAction !== requiredAction ||
      selected.isAutomationEnabled !== isAutomationEnabled ||
      selected.isLineSuggestionsEnabled !== isLineSuggestionsEnabled;

    if (hasRecentChanges && !(await confirmDiscardChanges())) {
      showToast('success', 'Inbox settings successfully saved!');
      return; // User canceled discarding changes
    }

    if (onCancel) {
      onCancel();
    }

    if (hasRecentChanges) {
      showToast('success', 'Changes have been discarded');
    }

    onChangeView('view');
  };

  const handleSwitchChange = (
    field: 'requiredAction' | 'isAutomationEnabled' | 'isLineSuggestionsEnabled'
  ) => {
    if (field === 'requiredAction') setRequiredAction(requiredAction ? 0 : 1);
    if (field === 'isAutomationEnabled') setIsAutomationEnabled(isAutomationEnabled ? 0 : 1);
    if (field === 'isLineSuggestionsEnabled') {
      const newValue = isLineSuggestionsEnabled ? 0 : 1;
      setIsLineSuggestionsEnabled(newValue);
    }
  };

  const onChangeView = (view) => {
    const selected = project.Selected;
    setRequiredAction(selected.requiredAction);
    setIsAutomationEnabled(selected.isAutomationEnabled);
    setIsLineSuggestionsEnabled(selected.isLineSuggestionsEnabled);
    setLayout(view);
    if (view === 'view' && triggerEdit) {
      triggerEdit({ inbox: false });
    }
  };

  // Executes on component mount only
  useEffect(() => {
    const selected = project.Selected;

    if (selected.id) {
      setRequiredAction(selected.requiredAction);
      setIsAutomationEnabled(selected.isAutomationEnabled);
      setIsLineSuggestionsEnabled(selected.isLineSuggestionsEnabled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When other sections are edited (opened), close this form
  useEffect(() => {
    const hasRecentChanges = hasChanges;
    if (!isOpen || isOpen === 'inbox') {
      return;
    }

    if (
      hasRecentChanges &&
      Object.keys(hasRecentChanges).filter((f) => hasRecentChanges[f]).length < 1
    ) {
      setLayout('view');
    }
  }, [isOpen, hasChanges]);

  // This effect runs when the 'isEdit' prop changes.
  // If 'isEdit' is 'inbox', it changes the view to 'form'.
  // Otherwise, it changes the view to 'view'.
  useEffect(() => {
    if (isEdit === 'inbox') {
      onChangeView('form');
    } else {
      onChangeView('view');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  const handleSave = async () => {
    const selected = project.Selected;
    const params = {
      requiredAction: requiredAction,
      isAutomationEnabled: isAutomationEnabled,
      isLineSuggestionsEnabled: isLineSuggestionsEnabled,
    };

    const saveInboxSettingResult = await saveInboxSettingMutation.mutateAsync({
      selectedId: selected.id,
      params,
    });

    if (!saveInboxSettingResult) return;

    dispatch({
      type: 'SET_PROJECT_SELECTED',
      Selected: {
        ...selected,
        requiredAction: requiredAction,
        isAutomationEnabled: isAutomationEnabled,
        isLineSuggestionsEnabled: isLineSuggestionsEnabled,
      },
    });
    showToast('success', 'Inbox settings successfully saved!');
    onChangeView('view');
  };

  const onEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (triggerEdit) {
      triggerEdit({ inbox: true });
    }

    if (Object.keys(hasChanges).filter((f) => hasChanges[f]).length > 0) {
      // eslint-disable-next-line no-undef
      $('#accountSetting').modal('show');
    } else {
      onChangeView('form');
    }
  };

  const selected = project.Selected || {};
  const hasRecentChanges =
    layout === 'form' &&
    (selected.requiredAction !== requiredAction ||
      Boolean(selected.isAutomationEnabled) !== Boolean(isAutomationEnabled) ||
      Boolean(selected.isLineSuggestionsEnabled) !== Boolean(isLineSuggestionsEnabled));

  const editAllowedRoles =
    loggedUser.data.userRole < 3 || project.Selected.leadId === loggedUser.data.id;

  if (editChange) {
    const changesProps = hasChanges;
    const changes = changesProps.inbox;

    if (changes !== hasRecentChanges) {
      editChange({ inbox: hasRecentChanges });
    }
  }

  const headerAction = (
    <HeaderAction
      isAllowedToEdit={editAllowedRoles}
      layout={layout}
      isActive={selected.isActive}
      disabled={!hasRecentChanges}
      onEdit={onEdit}
      onCancel={onDiscardChanges}
      onSave={handleSave}
    />
  );

  return (
    <React.Fragment>
      <SettingsCard id={'inbox'} title={'Inbox'} actionButton={headerAction}>
        <form
          id="inbox-form"
          className={clsx('tw-max-w-full tw-w-full', 'tw-py-[20px] tw-px-[30px]')}
        >
          {/* Require Action to close entry settings */}
          <InboxItemSettings
            content="Require action to close an entry?"
            isActive={requiredAction}
            layout={layout}
            isEnabled={selected.requiredAction}
            onSwitchChange={() => handleSwitchChange('requiredAction')}
          />

          {/* Enable/Disabled automation settings */}
          <AutomationSettings
            isActive={isAutomationEnabled}
            layout={layout}
            isEnabled={selected.isAutomationEnabled}
            onSwitchChange={() => handleSwitchChange('isAutomationEnabled')}
          />

          {/* Enable/Disable line suggestions/ prefill entry settings */}
          <PrefillSettings
            isActive={isLineSuggestionsEnabled}
            className={clsx('tw-mt-[20px]')}
            content={'Pre-fill from last entry'}
            layout={layout}
            isEnabled={selected.isLineSuggestionsEnabled}
            onSwitchChange={() => handleSwitchChange('isLineSuggestionsEnabled')}
          />
        </form>
      </SettingsCard>

      <DiscardChangesModal />
    </React.Fragment>
  );
};

export default Inbox;
